/** How the SEND report words and keys what it shows: counts, wire tokens, files and domain sets. */
import { _isNotEmpty, _notNil } from '@/littledash';
import { prettifyKey, StringUtils } from '@/utils/String.utils';
import { sendDatasetOf } from './SendDomains';

const COUNT_FORMAT = new Intl.NumberFormat('en-US');

/** A count as the report prints every count, grouped in thousands: "1,000", never "1000". */
export const formatCount = (count: number): string => COUNT_FORMAT.format(count);

/** `count`, formatted, and the word for it, by the shared plural rules. */
export const countOf = (count: number, one: string, other: string): string =>
  StringUtils.pluralize(count)`${formatCount(count)} ${{ one, other }}`;

/** Where in a file a finding or note points: its column and row, as far as it names them. */
export const findingLocationOf = (finding: {
  column_name?: string | null;
  row_number?: number | null;
}): string | undefined => {
  const parts = [
    _notNil(finding.column_name) ? finding.column_name : null,
    _notNil(finding.row_number) ? `row ${finding.row_number}` : null,
  ].filter((part): part is string => _notNil(part));
  return _isNotEmpty(parts) ? parts.join(', ') : undefined;
};

/** Categories whose token, read as words, would mislabel the finding. */
const FINDING_CATEGORY_LABELS: Readonly<Record<string, string>> = {
  duplicate_dataset: 'Dataset uploaded more than once',
};

/** What a finding's category reads as in the report: its own label where it has one, else its words. */
export const findingCategoryLabel = (category: string): string =>
  Object.hasOwn(FINDING_CATEGORY_LABELS, category) ? FINDING_CATEGORY_LABELS[category] : prettifyKey(category);

/**
 * A stable, unique key for each file of a result, in order. Usually its physical dataset (`DM`,
 * `SUPPMI`), but a bundle can hold two files of one dataset, so a repeat is numbered (`DM`, `DM-2`).
 */
export const fileKeysOf = (
  files: ReadonlyArray<{ domain_code: string; dataset_name?: string | null }>
): Array<string> => {
  const seen = new Map<string, number>();
  return files.map((file) => {
    const dataset = sendDatasetOf(file);
    const occurrence = (seen.get(dataset) ?? 0) + 1;
    seen.set(dataset, occurrence);
    return occurrence === 1 ? dataset : `${dataset}-${occurrence}`;
  });
};

/** The label for a set of domains a cross-domain check spanned, e.g. `DM ↔ LB` or `DM ↔ SE ↔ TA`. */
export const domainSetLabel = (domains: ReadonlyArray<string>): string => domains.join(' ↔ ');

/** What a note spanning every domain the bundle uploaded is about. */
export const WHOLE_BUNDLE = 'Whole bundle';

/**
 * The label for the domains an AI note spans: "Whole bundle" when they are every domain uploaded, as
 * the bundle's own summary always is, rather than a chain naming every one of them; otherwise
 * the domain set, as for a check.
 */
export const aiNoteDomainsLabel = (
  domains: ReadonlyArray<string>,
  uploaded: ReadonlyArray<{ domain_code: string }>
): string => {
  const uploadedDomains = new Set(uploaded.map(({ domain_code: domainCode }) => domainCode));
  const spanned = new Set(domains);
  const spansEveryDomain =
    uploadedDomains.size > 0 &&
    spanned.size === uploadedDomains.size &&
    [...uploadedDomains].every((domain) => spanned.has(domain));
  return spansEveryDomain ? WHOLE_BUNDLE : domainSetLabel(domains);
};
